import { colors } from '../../../colors';
import InputField from '../../../components/Form/InputField';
import { FaSearch } from 'react-icons/fa';
import { formatNumber } from '../../../helpers/NumberFormatter';
import LoadingIcons from 'react-loading-icons';
import useWidthBreakpoint from '../../../hooks/useWidthBreakpoint';
import { useCallback, useMemo, useState } from 'react';

interface Props {
  title: string;
  availableStableFunds: Record<string, Record<string, number>>;
  query: string | undefined;
  setQuery: React.Dispatch<React.SetStateAction<string | undefined>>;
  isLoading: boolean;
  credits: number | undefined;
}

const DiscoverHeader = (props: Props) => {
  const { title, availableStableFunds, query, setQuery, isLoading, credits } =
    props;

  const { isMatch } = useWidthBreakpoint();
  const [searchQuery, setSearchQuery] = useState<string | undefined>(query);

  // The request to fetch the bots is heavy
  // So we don't want to re fetch them every time the user changes the query
  const handleQueryUpdate = useCallback(() => {
    setQuery(searchQuery);
  }, [searchQuery, setQuery]);

  const searchField = useMemo(() => {
    return {
      label: 'Search',
      value: searchQuery,
      handleValueChange: (value: string) => {
        if (value.length > 12) {
          return;
        }

        setSearchQuery(value.toUpperCase());
      },
      shouldFit: true,
      handleEnterPress: handleQueryUpdate,
    };
  }, [handleQueryUpdate, searchQuery]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: !isMatch('md') ? 'column' : 'row',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        backgroundColor: colors.black,
        marginLeft: -30,
        marginRight: -30,
        paddingLeft: 30,
        paddingRight: !isMatch('md') ? 36 : 36,
        paddingBottom: 24,
      }}
    >
      <div>
        <p
          style={{
            color: colors.white,
            fontSize: 32,
            fontWeight: 'bold',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          {title}
        </p>
        {!!Object.keys(availableStableFunds).length && (
          <div style={{ color: colors.white }}>
            {Object.entries(availableStableFunds).map(([exchange, quotes]) => (
              <p
                key={exchange}
                style={{
                  marginRight: 32,
                  margin: 0,
                  marginTop: 4,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    textTransform: 'capitalize',
                    fontSize: 16,
                    color: colors.gray,
                    marginRight: 12,
                  }}
                >
                  {exchange}:
                </span>
                {Object.entries(quotes).map(([quote, value]) => (
                  <span style={{ marginRight: 12 }} key={quote}>
                    {formatNumber(value, 2)}{' '}
                    <span
                      style={{
                        color: colors.gray,
                        fontSize: 14,
                        fontWeight: 'bold',
                      }}
                    >
                      {quote}
                    </span>
                  </span>
                ))}
              </p>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 26 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <InputField config={searchField} width={'100%'} />
          <button
            onClick={handleQueryUpdate}
            style={{
              backgroundColor: '#0047AB',
              border: '1px solid #0047AB',
              borderRadius: 4,
              marginLeft: 12,
              cursor: 'pointer',
              height: 56,
              aspectRatio: 1,
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingIcons.Grid width={20} height={20} />
            ) : (
              <FaSearch color={colors.white} size={20} />
            )}
          </button>
        </div>
        {typeof credits !== 'undefined' && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              marginTop: 8,
            }}
          >
            <p style={{ margin: 0, padding: 0, color: colors.white }}>
              Available credits:{' '}
              <span style={{ color: credits < 0 ? colors.red : colors.green }}>
                {credits}
              </span>
              /50
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoverHeader;
